import { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from 'App';
import ROLES from 'config/roles';
import { isDynamicModificationEnabled } from 'lib/useDynamicModificationRules';
import useRoles from 'lib/useRoles';
import inspectionsService from './inspectionsService';

const EMPTY_SUMMARY = { asked: [], skipped: [] };
// Who also sees where each rule question stands now for the product and factory.
const CURRENT_STAGE_ROLES = [ROLES.QM_ADMIN, ROLES.CLIENT_ADMIN, ROLES.COORDINATOR];
const ruleKey = (ruleId, templateId) => `${ruleId}:${templateId}`;

/**
 * What dynamic modification rules did to one inspection: the questions they
 * skipped, and the stage each asked question ran at. Admins and coordinators
 * also get where each rule question stands now.
 */
export default function useInspectionDynamicModification(inspectionId) {
  const { appState } = useContext(AppContext);
  const isEnabled = isDynamicModificationEnabled(appState);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const { isAllowed } = useRoles();
  const canSeeCurrent = isEnabled && isAllowed(CURRENT_STAGE_ROLES);
  const [current, setCurrent] = useState([]);

  useEffect(() => {
    if (!isEnabled || !inspectionId) return;
    let isMounted = true;
    inspectionsService
      .getInspectionDynamicModification(inspectionId)
      .then((loaded) => isMounted && setSummary(loaded))
      .catch(() => isMounted && setSummary(EMPTY_SUMMARY));
    return () => {
      isMounted = false;
    };
  }, [isEnabled, inspectionId]);

  useEffect(() => {
    if (!canSeeCurrent || !inspectionId) return;
    let isMounted = true;
    inspectionsService
      .getInspectionDynamicModificationCurrent(inspectionId)
      .then((loaded) => isMounted && setCurrent(loaded))
      .catch(() => isMounted && setCurrent([]));
    return () => {
      isMounted = false;
    };
  }, [canSeeCurrent, inspectionId]);

  const currentByKey = useMemo(
    () => new Map(current.map((row) => [ruleKey(row.ruleId, row.templateId), row])),
    [current],
  );

  const askedById = useMemo(
    () => new Map(summary.asked.map((asked) => [`${asked.questionId}`, asked])),
    [summary.asked],
  );

  return {
    skipped: summary.skipped,
    stageOf: (questionId) => askedById.get(`${questionId}`) || null,
    currentOf: (ruleId, templateId) =>
      (ruleId && currentByKey.get(ruleKey(ruleId, templateId))) || null,
  };
}
